import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FolderOpen, CircleHelp, Menu } from "lucide-react";
import ModeSwitch from "@/components/creator/ModeSwitch";
import WorkflowSpine from "@/components/creator/WorkflowSpine";
import StudioAssistant from "@/components/creator/assistant/StudioAssistant";
import AuthMenu from "@/components/AuthMenu";
import ElpmLogo from "@/components/ElpmLogo";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { SIMPLE_SECTIONS, INFO_LINKS } from "@/lib/creatorConstants";
import { getCurrentSongId, proStudioPath } from "@/lib/studioMode";

const PAGE_INIT = { opacity: 0, x: 28 };
const PAGE_IN = { opacity: 1, x: 0 };
const PAGE_TRANSITION = { type: "spring", damping: 28, stiffness: 260 };


function sectionTarget(s, songId) {
  if (!s.to) return proStudioPath(songId, s.intent);
  return s.key === "master" && songId ? `${s.to}/${songId}` : s.to;
}

function isActive(s, pathname) {
  if (!s.to) return false;
  if (s.exact) return pathname === s.to;
  return [s.to, ...(s.also || [])].some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function SectionNav({ className = "", vertical = false, onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const songId = getCurrentSongId();
  return (
    <nav data-testid={vertical ? "creator-sections-mobile" : "creator-sections"} aria-label="Studio sections" className={`flex gap-1 ${vertical ? "flex-col items-stretch" : "items-center"} ${className}`}>
      {SIMPLE_SECTIONS.map((s) => {
        const active = isActive(s, location.pathname);
        return (
          <button
            key={s.key}
            type="button"
            data-testid={`${vertical ? "mobile-" : ""}creator-section-${s.key}`}
            aria-current={active ? "page" : undefined}
            onClick={() => { onNavigate?.(); navigate(sectionTarget(s, songId)); }}
            className={`whitespace-nowrap rounded-full px-3.5 py-2 text-left font-display text-[14px] font-medium tracking-wide transition-colors duration-200 ${
              active ? "bg-white/[0.12] text-white" : "text-white/80 hover:bg-white/[0.08] hover:text-white"
            }`}
          >
            {s.label}
          </button>
        );
      })}
    </nav>
  );
}

function MobileNav() {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" data-testid="creator-mobile-menu" aria-label="Open menu" className="grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-white/[0.06] text-white/85 md:hidden">
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] border-white/10 bg-[#05070f] text-white">
        <SheetTitle className="mb-4"><ElpmLogo className="h-9" testId="mobile-menu-logo" /><span className="sr-only">ELPM Studio</span></SheetTitle>
        <SectionNav vertical onNavigate={() => setOpen(false)} />
      </SheetContent>
    </Sheet>
  );
}

function CreatorFooter() {
  return (
    <footer data-testid="creator-footer" className="mx-auto mt-16 max-w-[1400px] border-t border-white/[0.08] px-4 py-8 sm:px-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[13px] text-white/70">© {new Date().getFullYear()} ELPM Studio · Make your song, your way.</p>
        <nav aria-label="Information" className="flex flex-wrap gap-x-5 gap-y-2">
          {INFO_LINKS.map((l) => (
            <Link key={l.key} to={l.to} data-testid={`footer-link-${l.key}`} className="text-[13px] font-medium text-white/80 underline-offset-4 transition-colors hover:text-white hover:underline">
              {l.label}
            </Link>
          ))}
        </nav>
      </div>
    </footer>
  );
}

/**
 * Page frame for every ELPM Creator screen: back link, section nav,
 * Simple|Advanced switch, auth chip, optional workflow spine, sliding content.
 * The `creator-door` class switches on the larger, brighter text layer in index.css.
 */
export default function CreatorShell({ children, back = null, spine = null, spineDone = null, title = null, subtitle = null, wide = false, testId = "creator-page" }) {
  const songId = getCurrentSongId();
  return (
    <div data-testid={testId} className="creator-door relative min-h-screen overflow-x-hidden text-white">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -left-40 top-[-120px] h-[520px] w-[520px] rounded-full bg-blue-600/20 blur-[140px]" />
        <div className="absolute right-[-160px] top-[30%] h-[560px] w-[560px] rounded-full bg-violet-600/20 blur-[160px]" />
        <div className="absolute bottom-[-200px] left-[30%] h-[420px] w-[420px] rounded-full bg-cyan-500/10 blur-[140px]" />
      </div>

      <header className="sticky top-0 z-30 border-b border-white/[0.08] bg-[#05070f]/80 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <MobileNav />
            {back ? (
              <Link to={back} data-testid="creator-back" aria-label="Back" className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-white/15 bg-white/[0.06] text-white/85 transition-colors hover:bg-white/[0.14] hover:text-white">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            ) : null}
            <Link to="/" data-testid="creator-logo" className="flex flex-shrink-0 items-center gap-2" aria-label="ELPM Studio home">
              <ElpmLogo className="h-8 sm:h-10" testId="creator-logo-img" />
            </Link>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <Link
              to="/create/projects"
              data-testid="creator-nav-projects"
              className="hidden whitespace-nowrap items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-2 font-display text-[13px] font-medium text-white/85 transition-colors hover:bg-white/[0.14] hover:text-white lg:flex"
            >
              <FolderOpen className="h-4 w-4" /> My Music Library
            </Link>
            <Link
              to="/about"
              data-testid="creator-nav-info"
              className="hidden whitespace-nowrap items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-2 font-display text-[13px] font-medium text-white/85 transition-colors hover:bg-white/[0.14] hover:text-white xl:flex"
            >
              <CircleHelp className="h-4 w-4" /> Help &amp; Info
            </Link>
            <StudioAssistant />
            <ModeSwitch current="simple" songId={songId} />
            <AuthMenu />
          </div>
        </div>
        {/* Section tabs live on their own row so they never collide with the account pills. */}
        <div className="mx-auto hidden max-w-[1400px] px-4 pb-2 sm:px-6 md:block">
          <SectionNav className="flex-wrap" />
        </div>
      </header>

      <motion.main
        initial={PAGE_INIT}
        animate={PAGE_IN}
        transition={PAGE_TRANSITION}
        className={`mx-auto px-4 pb-12 pt-8 sm:px-6 sm:pt-10 ${wide ? "max-w-[1400px]" : "max-w-6xl"}`}
      >
        {spine ? <div className="mb-8"><WorkflowSpine active={spine} done={spineDone} /></div> : null}
        {title ? (
          <div className="mb-8 max-w-3xl">
            <h1 data-testid="creator-title" className="font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">{title}</h1>
            {subtitle ? <p className="mt-3 text-base text-white/80 md:text-lg">{subtitle}</p> : null}
          </div>
        ) : null}
        {children}
      </motion.main>
      <CreatorFooter />
    </div>
  );
}
